import type {
    Pointer,
    Variable,
    CompRef,
    VariantPointer,
    VariableType,
    RawVariableConnection,
    VariableConnections
} from '@wix/document-services-types'
import {
    DalItem,
    DalValue,
    DmApis,
    Extension,
    ExtensionAPI,
    ExtensionPublicAPI,
    pointerUtils,
    ValidateValue
} from '@wix/document-manager-core'
import _ from 'lodash'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {HooksExtensionApi} from '../hooks/hooks'
import type {RelationshipsAPI} from '../relationships'
import {getIdFromRef} from '../../utils/dataUtils'
import * as constants from '../../constants/constants'
import * as refArrayUtils from '../../utils/refArrayUtils'
import {NAMESPACES_SUPPORTING_VARIABLES, VARIABLES_LIST_TYPE} from './constants'
import {DATA_MODEL_HOOKS, DeserializeDataItemEvent} from '../dataModel/hooks'
import {ReportableError} from '@wix/document-manager-utils'
import {deepClone} from '@wix/wix-immutable-proxy'
import {collectVariableConnections, getChildrenTree, getParentsTree} from './variablesRelations'
import {VIEW_MODES} from '../../constants/constants'

const {getPointer} = pointerUtils

const VARIABLES_NAMESPACE = constants.DATA_TYPES.variables
const REF_ARRAY_TYPE = constants.REF_ARRAY_DATA_TYPE
const VARIANT_RELATION_TYPE = constants.RELATION_DATA_TYPES.VARIANTS
const {DESKTOP, MOBILE} = constants.VIEW_MODES

const getVariableConnectionPaths = (dataItem: DalItem, path: string[] = []): RawVariableConnection[] => {
    if (!dataItem || typeof dataItem !== 'object') return []

    const connections: RawVariableConnection[] = []

    if (Array.isArray(dataItem)) {
        dataItem.forEach((item, idx) => {
            connections.push(...getVariableConnectionPaths(item, [...path, idx.toString()]))
        })
        return connections
    }

    if (dataItem.variableConnections) {
        dataItem.variableConnections.forEach((connection: RawVariableConnection) => {
            connections.push({
                path: [...path, connection.path].join('/'),
                variableId: connection.variableId
            })
        })
    }

    Object.entries(dataItem).forEach(([key, value]) => {
        if (key !== 'variableConnections' && typeof value === 'object' && value !== null) {
            connections.push(...getVariableConnectionPaths(value, [...path, key]))
        }
    })

    return connections
}

const createVariablesError = (message: string) => {
    return new ReportableError({
        message,
        errorType: 'variablesError'
    })
}

const createExtension = (): Extension => {
    const getCompPointersByDataItem = (
        relationships: RelationshipsAPI['relationships'],
        pointer: Pointer
    ): Pointer[] => {
        if (pointer.type === DESKTOP || pointer.type === MOBILE) {
            return [pointer]
        }

        const references = relationships.getOwningReferencesToPointer(pointer)
        return references.flatMap(p => getCompPointersByDataItem(relationships, p))
    }

    const comparePointers = (p1: Pointer, p2: Pointer) => p1.type === p2.type && p1.id === p2.id

    const createExtensionAPI = ({extensionAPI, pointers, dal}: DmApis): VariablesExtensionAPI => {
        const {relationships} = extensionAPI as RelationshipsAPI
        const {dataModel} = extensionAPI as DataModelExtensionAPI

        // Helper functions for variable resolution
        const validateCompPointer = (componentPointer: Pointer) => {
            if (!componentPointer || !dal.has(componentPointer)) {
                throw createVariablesError('The component pointer is not passed or it does not exist')
            }
        }

        const getComponentVariablesIds = (componentPointer: Pointer): string[] => {
            const variablesItem = dataModel.components.getItem(componentPointer, VARIABLES_NAMESPACE)
            if (!variablesItem) {
                return []
            }
            return variablesItem.variables.map((variant: DalValue) => variant.id)
        }

        const validateCompAndVariablePointers = (componentPointer: Pointer, variablePointer: Pointer) => {
            validateCompPointer(componentPointer)
            if (!variablePointer || !dal.has(variablePointer)) {
                throw createVariablesError('The variable pointer is not passed or it does not exist')
            }
            const currentVariableIds = getComponentVariablesIds(componentPointer)
            if (!_.includes(currentVariableIds, variablePointer.id)) {
                throw createVariablesError(
                    `The component ${componentPointer.id} didn't define ${variablePointer.id} variable`
                )
            }
        }

        const validateVariants = (compPointerWithVariants: VariantPointer) => {
            _.forEach(compPointerWithVariants.variants, variant => {
                if (!dal.has(variant)) {
                    throw createVariablesError(`Passing component with non-existing variant ${variant.id}`)
                }
            })
        }

        const getVariableValueType = (variableType: string) => {
            const referenceFieldsInfo = (extensionAPI as any).schemaAPI.extractReferenceFieldsInfo(
                VARIABLES_NAMESPACE,
                variableType
            )
            const reference = _.find(
                referenceFieldsInfo,
                ref => _.isEqual(ref.path, ['value']) && !_.isEmpty(ref.refTypes)
            )
            if (!reference) {
                throw createVariablesError(`${variableType} should define the variable value type in the schema`)
            }
            return reference.refTypes[0]
        }

        const resolveVariableConnections = (
            compPointerWithVariants: CompRef,
            variablePointersToResolve: {propertyPath: string; variablePointer: Pointer}[],
            resolvedValue: any,
            visitedVariables: Set<string>
        ): void => {
            for (const {propertyPath, variablePointer} of variablePointersToResolve) {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    const referencedVariableData = getVariableData(
                        compPointerWithVariants,
                        variablePointer,
                        true,
                        visitedVariables
                    )
                    if (referencedVariableData?.value) {
                        _.set(resolvedValue, propertyPath, referencedVariableData.value)
                    }
                } catch (error) {
                    const message = `Failed to resolve variable ${variablePointer.id}: ${(error as Error).toString()}`
                    ;(extensionAPI as any).logger.captureError(createVariablesError(message))
                }
            }
        }

        const resolveVariables = (
            compPointerWithVariants: CompRef,
            value: any,
            visitedVariables = new Set<string>()
        ): any => {
            if (!value || typeof value !== 'object') {
                return value
            }

            if (Array.isArray(value)) {
                return value.map(item => resolveVariables(compPointerWithVariants, item, visitedVariables))
            }

            const resolvedValue = deepClone(value)
            let currentVisitedVariables = visitedVariables

            if (value.variableConnections && typeof value.variableConnections === 'object') {
                currentVisitedVariables = new Set(visitedVariables)

                // Phase 1: Collect all variable IDs and pointers from variableConnections
                const variablePointersToResolve = collectVariableConnections(dal, value.variableConnections)
                const variableIdsToResolve = variablePointersToResolve.map(({variablePointer}) => variablePointer.id)

                // Phase 2: Validate circular references and add to visited set
                for (const variableId of variableIdsToResolve) {
                    if (currentVisitedVariables.has(variableId)) {
                        throw createVariablesError('Circular variable reference detected')
                    }
                    currentVisitedVariables.add(variableId)
                }

                // Phase 3: Resolve variable connections and update the resolved value
                resolveVariableConnections(
                    compPointerWithVariants,
                    variablePointersToResolve,
                    resolvedValue,
                    currentVisitedVariables
                )

                delete resolvedValue.variableConnections
            }

            // Recursively process nested objects using the updated visited variables
            for (const [key, nestedValue] of Object.entries(resolvedValue)) {
                if (key !== 'variableConnections' && typeof nestedValue === 'object' && nestedValue !== null) {
                    resolvedValue[key] = resolveVariables(compPointerWithVariants, nestedValue, currentVisitedVariables)
                }
            }

            return resolvedValue
        }

        const getVariableData = (
            compPointerWithVariants: CompRef,
            variablePointer: Pointer,
            resolveConnections: boolean = true,
            visitedVariables?: Set<string>
        ) => {
            const {variants} = extensionAPI as VariantsExtensionAPI

            validateCompAndVariablePointers(compPointerWithVariants, variablePointer)
            validateVariants(compPointerWithVariants)

            // Create pointer with variants if needed
            const variablePointerWithVariants = compPointerWithVariants.variants
                ? {
                      ...variablePointer,
                      variants: compPointerWithVariants.variants
                  }
                : variablePointer

            // Get the data considering variants
            const variableDataItemInVariant = variants.getItemConsideringVariantsForPointer(
                variablePointerWithVariants,
                VARIABLES_NAMESPACE,
                'value'
            )

            if (!variableDataItemInVariant) {
                return null
            }

            const variableDefinition = dal.get(variablePointer)
            const variableValueType = getVariableValueType(variableDefinition.type)

            if (!resolveConnections) {
                return {
                    type: variableValueType,
                    value: variableDataItemInVariant.value,
                    variableConnections: variableDataItemInVariant.variableConnections
                }
            }
            const resolvedValue = resolveVariables(compPointerWithVariants, variableDataItemInVariant, visitedVariables)

            return {
                type: variableValueType,
                value: resolvedValue.value
            }
        }

        const getComponentsUsingVariable = (variablePointer: Pointer, viewMode: string = DESKTOP): Pointer[] =>
            _(relationships.getReferencesToPointer(variablePointer))
                .filter(({type}) => type !== VARIABLES_NAMESPACE)
                .flatMap(pointer => getCompPointersByDataItem(relationships, pointer))
                .filter(({type}) => type === viewMode)
                .uniqWith(comparePointers)
                .value()

        const getAllVariablesPointers = (componentPointer: Pointer, filter: {type?: VariableType} = {}): Pointer[] => {
            const variablesListItem = dataModel.components.getItem(componentPointer, VARIABLES_NAMESPACE)
            let variables = variablesListItem?.variables ?? []
            if (variables && filter.type) {
                variables = _.filter(variables, ({type}) => type && filter.type === type)
            }
            return _.map(variables, ({id}) => getPointer(getIdFromRef(id), VARIABLES_NAMESPACE))
        }

        const collectAncestorsVariablesIds = (componentPointer: Pointer) => {
            const ancestors = [componentPointer, ...pointers.structure.getAncestors(componentPointer)].filter(
                pointer => !_.isNil(pointer)
            )
            const variablesIds = _.reduce(
                ancestors,
                (vars: string[], ancestor) => {
                    const variablesItem = dataModel.components.getItem(ancestor, VARIABLES_NAMESPACE)
                    if (variablesItem) {
                        const compVariables = variablesItem.variables.map((variant: DalValue) => variant.id)
                        vars.push(...compVariables)
                    }
                    return vars
                },
                []
            )
            return _.uniq(variablesIds)
        }

        const getVariablesConnectedToItem = (pointerWithVariants: Pointer): VariableConnections => {
            const dataItem = dataModel.getItem(
                pointerWithVariants.id,
                pointerWithVariants.type,
                constants.MASTER_PAGE_ID,
                undefined,
                undefined,
                true
            )

            if (refArrayUtils.refArray.isRefArray(dataItem?.value)) {
                const {variants} = extensionAPI as VariantsExtensionAPI

                const variantPointer = variants.getDataPointerConsideringVariants(
                    pointerWithVariants,
                    pointerWithVariants.type
                )!

                dataItem.value = dataModel.getItem(
                    variantPointer.id,
                    variantPointer.type,
                    constants.MASTER_PAGE_ID,
                    undefined,
                    undefined,
                    true
                )
            }

            const variableConnections = getVariableConnectionPaths(dataItem)

            return variableConnections.reduce(
                (variableConnectionsObject: VariableConnections, {path, variableId}: RawVariableConnection) => ({
                    ...variableConnectionsObject,
                    [path]: pointerUtils.getPointer(variableId, VARIABLES_NAMESPACE)
                }),
                {}
            )
        }

        const connect = (obj: any, property: string, variablePointer: Pointer) => {
            if (_.isNil(variablePointer) || !dal.get(variablePointer)) {
                throw createVariablesError('The variable pointer is not passed or it does not exist')
            }

            if (!_.has(obj, property)) {
                throw createVariablesError('Property does not exist on object')
            }

            if (_.isArray(obj)) {
                return _.set(obj, [property, 'variableConnections', ''], variablePointer.id)
            }

            return _.set(obj, ['variableConnections', property], variablePointer.id)
        }

        const getGlobalVariableIds = (): string[] => {
            const masterPagePointer = pointers.structure.getMasterPage(VIEW_MODES.DESKTOP)
            return getComponentVariablesIds(masterPagePointer)
        }

        return {
            variables: {
                getComponentsUsingVariable,
                list: getAllVariablesPointers,
                getGlobalVariableIds,
                collectAncestorsVariablesIds,
                getVariableData,
                getComponentVariablesIds,
                getVariableValueType,
                getVariablesConnectedToItem,
                resolveVariables,
                connect
            }
        }
    }

    const createValidator = ({dal, pointers, extensionAPI, coreConfig}: DmApis): Record<string, ValidateValue> => {
        const {experimentInstance} = coreConfig
        const {relationships} = extensionAPI as RelationshipsAPI

        const variablesListPath = ['variables']
        const variableTypes = dal.schema
            .extractReferenceFieldsInfo(VARIABLES_NAMESPACE, VARIABLES_LIST_TYPE)
            .find(({path}) => _.isEqual(path, variablesListPath))!.refTypes
        const variableTypesSet = new Set(variableTypes)

        const isEligibleForTypeValidator = (pointer: Pointer, value?: DalValue) =>
            value?.type && pointer.type === VARIABLES_NAMESPACE && value.type !== VARIANT_RELATION_TYPE

        const createValidatorResult = (type: string, message: string) => [
            {
                shouldFail: true,
                type,
                message,
                extras: {}
            }
        ]

        const createTypeValidatorResult = (type: string) =>
            createValidatorResult(type, 'Variable type is not matching the value type')

        const getIdsByRefArrayValues = (refArrayValues: string[]) => {
            return refArrayValues.map((id: string) => {
                const value = dal.get(getPointer(id, VARIABLES_NAMESPACE))
                if (value.type !== VARIANT_RELATION_TYPE) return id

                return refArrayUtils.variantRelation.extractTo(value)
            })
        }

        const getRefArrayByVariable = (variable: Variable) => {
            const references = dal.schema.getReferences(VARIABLES_NAMESPACE, variable)[0]
            if (!references) return
            const {id, refInfo} = references
            const refArrayPointer = getPointer(id, VARIABLES_NAMESPACE)
            const refArray = dal.get(refArrayPointer)
            const refArrayValues = refArrayUtils.refArray.extractValuesWithoutHash(refArray)
            const varValIds = getIdsByRefArrayValues(refArrayValues)

            return {
                refArray,
                varValIds,
                refInfo
            }
        }

        const getRelationalSplitNodePointer = (pointer: Pointer) => {
            const ownerPointer = relationships.getOwningReferencesToPointer(pointer, pointer.type)[0]

            if (!ownerPointer) return

            const ownerObj = dal.get(ownerPointer)

            if (ownerObj.type === VARIANT_RELATION_TYPE) {
                const splittingNodeId = refArrayUtils.variantRelation.extractFrom(ownerObj)

                return getPointer(splittingNodeId, pointer.type)
            }

            if (ownerObj.type === REF_ARRAY_TYPE) {
                return relationships.getOwningReferencesToPointer(ownerPointer, pointer.type)[0]
            }
        }

        const extractRelationalSplitSchemaInfo = (pointer: Pointer, value: DalValue) =>
            dal.schema
                .extractReferenceFieldsInfo(pointer.type, value.type)
                ?.find(({isRelationalSplit}) => isRelationalSplit)

        const isEqualOrDescendant = (compPointer: Pointer, possibleAncestorPointer: Pointer) =>
            comparePointers(compPointer, possibleAncestorPointer) ||
            pointers.structure.isDescendant(compPointer, possibleAncestorPointer)

        const checkCompDescendentOfOwnersByViewMode = (
            compPointer: Pointer,
            variableOwnerCompsPointers: Pointer[],
            viewMode: string
        ) =>
            _(variableOwnerCompsPointers)
                .filter(({type}) => type === viewMode)
                .every(
                    ownerCompPointer =>
                        (experimentInstance.isOpen('dm_allowReferencingOutOfScopeMasterPageVariables') &&
                            pointers.structure.isMasterPage(ownerCompPointer)) ||
                        isEqualOrDescendant(compPointer, ownerCompPointer)
                )

        const getReferredVariablePointers = (pointer: Pointer) => {
            const referredPointers = relationships.getReferredPointers(pointer)
            return referredPointers.filter(
                referredPointer =>
                    referredPointer.type === VARIABLES_NAMESPACE && variableTypesSet.has(dal.get(referredPointer)?.type)
            )
        }

        const getVariableOwnerComps = (variablePointers: Pointer[]) => {
            return _(variablePointers)
                .flatMap(variablePointer =>
                    relationships.getOwningReferencesToPointer(variablePointer, VARIABLES_NAMESPACE)
                )
                .flatMap(variablesListPointers => relationships.getOwningReferencesToPointer(variablesListPointers))
                .uniqWith(comparePointers)
                .value()
        }

        const validateVariableType = (pointer: Pointer, value?: DalValue) => {
            if (!isEligibleForTypeValidator(pointer, value) || !variableTypesSet.has(value.type)) return

            const {varValIds, refInfo} = getRefArrayByVariable(value) ?? {}
            const varValTypes =
                refInfo &&
                varValIds?.map(varValId => dal.get(getPointer(varValId, VARIABLES_NAMESPACE, {innerPath: ['type']})))
            if (!varValTypes || varValTypes.some(varValType => !refInfo.refTypes.includes(varValType))) {
                return createTypeValidatorResult('validateVariableType')
            }
        }

        const validateVariableValueType = (pointer: Pointer, value?: DalValue) => {
            if (!isEligibleForTypeValidator(pointer, value)) return

            const relationalSplitNodePointer = getRelationalSplitNodePointer(pointer)
            const relationalSplitNode = relationalSplitNodePointer && dal.get(relationalSplitNodePointer)
            if (!relationalSplitNode) return

            const {refTypes} = extractRelationalSplitSchemaInfo(pointer, relationalSplitNode) ?? {}

            if (refTypes && !refTypes.includes(value.type)) {
                return createTypeValidatorResult('validateVariableValueType')
            }
        }

        const validateVariableHierarchicalUsage = (pointer: Pointer, value?: DalValue) => {
            // Check if the given value is eligible for the validator.
            if (!value || !NAMESPACES_SUPPORTING_VARIABLES.has(pointer.type)) return

            // Find the variables that are in use by this data item.
            const referredVariablePointers = getReferredVariablePointers(pointer)
            if (referredVariablePointers.length === 0) return

            // Find the components that own the used variables.
            const variableOwnerCompsPointers = getVariableOwnerComps(referredVariablePointers)

            // Get the component pointer of the data item (could be DESKTOP and MOBILE components).
            const variableUserCompsPointers = getCompPointersByDataItem(relationships, pointer)

            if (_.every(variableOwnerCompsPointers, (owner: Pointer) => pointers.structure.isMasterPage(owner))) {
                return
            }

            // Make sure that the component that uses the variables is a descendent of the owners (in both DESKTOP and MOBILE),
            // or that the variable is owned by masterPage.
            for (const compPointer of variableUserCompsPointers) {
                if (!checkCompDescendentOfOwnersByViewMode(compPointer, variableOwnerCompsPointers, compPointer.type)) {
                    return createValidatorResult('validateVariableHierarchicalUsage', 'Variable is used out of scope')
                }
            }
        }

        return {
            validateVariableType,
            validateVariableValueType,
            validateVariableHierarchicalUsage
        }
    }

    const initialize = async ({extensionAPI, dal, pointers}: DmApis): Promise<void> => {
        const {hooks} = extensionAPI as HooksExtensionApi

        const findAndRemoveInnerVariableConnections = (
            prop: any,
            rootVariableConnections: any[],
            oldToNewIdMap?: Record<string, string>,
            path: any[] = []
        ) => {
            if (_.isArray(prop))
                prop.forEach((p, i) =>
                    findAndRemoveInnerVariableConnections(p, rootVariableConnections, oldToNewIdMap, [...path, i])
                )
            if (!_.isPlainObject(prop)) return

            const {variableConnections} = prop
            if (_.isPlainObject(variableConnections)) {
                delete prop.variableConnections
                _.forOwn(variableConnections, (variableId, property) => {
                    const newVariableId = oldToNewIdMap ? oldToNewIdMap[variableId] : variableId
                    if (newVariableId) {
                        rootVariableConnections.push({path: [...path, property].join('/'), variableId: newVariableId})
                    }
                })
            }

            _.forOwn(prop, (p, key) =>
                findAndRemoveInnerVariableConnections(p, rootVariableConnections, oldToNewIdMap, [...path, key])
            )
        }

        const deserializeVariablesConnections = ({
            deserializedDataItem,
            itemType,
            itemId,
            oldToNewIdMap
        }: DeserializeDataItemEvent) => {
            if (!NAMESPACES_SUPPORTING_VARIABLES.has(itemType)) return deserializedDataItem

            const rootVariableConnections: any[] = []
            findAndRemoveInnerVariableConnections(deserializedDataItem, rootVariableConnections, oldToNewIdMap)

            const variableConnectionsPointer = pointers.getPointer(itemId, itemType, {
                innerPath: ['variableConnections']
            })
            if (rootVariableConnections.length > 0 || dal.has(variableConnectionsPointer)) {
                deserializedDataItem.variableConnections = rootVariableConnections
            }

            return deserializedDataItem
        }

        const serializeVariableConnectionsInDataItem = ({dataItem}: any) => {
            const {variableConnections} = dataItem ?? {}
            if (!_.isArray(variableConnections)) return

            delete dataItem.variableConnections

            for (const {path, variableId} of variableConnections) {
                const splittedPath = path.split('/')
                splittedPath.splice(splittedPath.length - 1, 0, constants.VARIABLE_CONNECTIONS)
                _.set(dataItem, splittedPath, variableId)
            }
        }

        hooks.registerHook(DATA_MODEL_HOOKS.DESERIALIZE.DATA_ITEM_AFTER.id, deserializeVariablesConnections)
        hooks.registerHook(DATA_MODEL_HOOKS.SERIALIZE.DATA_ITEM_AFTER.id, serializeVariableConnectionsInDataItem)
    }

    const createPublicAPI = ({extensionAPI, pointers, dal}: DmApis): ExtensionPublicAPI => {
        const {variables} = extensionAPI as VariablesExtensionAPI

        return {
            variables: {
                getVariablesConnectedToItem: (dataItemPointer: Pointer) =>
                    variables.getVariablesConnectedToItem(dataItemPointer)
            },
            global: {
                variables: {
                    list: (filter: {type?: VariableType} = {}) => {
                        const masterPagePointer = pointers.components.getMasterPage(VIEW_MODES.DESKTOP)
                        return variables.list(masterPagePointer, filter)
                    },
                    getParentVariables: (variablePointer: Pointer) => {
                        const masterPagePointer = pointers.components.getMasterPage(VIEW_MODES.DESKTOP)
                        const vars = variables.list(masterPagePointer)
                        return getParentsTree(dal, extensionAPI, variablePointer, vars, masterPagePointer)
                    },
                    getChildVariables: (variablePointer: Pointer) => {
                        const masterPagePointer = pointers.components.getMasterPage(VIEW_MODES.DESKTOP)
                        const vars = variables.list(masterPagePointer)
                        return getChildrenTree(dal, extensionAPI, variablePointer, vars, masterPagePointer)
                    }
                }
            }
        }
    }

    return {
        name: 'variables',
        dependencies: new Set(['structure', 'relationships', 'dataModel', 'variants']),
        createExtensionAPI,
        createPublicAPI,
        createValidator,
        initialize
    }
}

export interface VariablesExtensionAPI extends ExtensionAPI {
    variables: {
        getComponentsUsingVariable(variablePointer: Pointer, viewMode?: string): Pointer[]
        list(componentPointer: Pointer, filter?: {type?: VariableType}): Pointer[]
        getGlobalVariableIds(): string[]
        collectAncestorsVariablesIds(componentPointer: Pointer): string[]
        getVariableData(compPointerWithVariants: CompRef, variablePointer: Pointer, resolveConnections?: boolean): any
        getComponentVariablesIds(componentPointer: Pointer): string[]
        getVariableValueType(variableType: string): string
        connect(obj: any, property: string, variablePointer: Pointer): any
        getVariablesConnectedToItem(dataItemPointer: Pointer): VariableConnections
        resolveVariables(compPointerWithVariants: CompRef, value: any): any
    }
}

export {createExtension}
